// maths.portll.net — the year's published tasks, grouped by strand, each with a
// derived state chip.
//
// The app never writes a "status" onto a task: the family's own records ARE the
// state. deriveTaskState reads the live completions + datasets snapshots and
// answers Not started / Planned / Data to enter / Done for each task. The one
// marker with no cloud home is "Planned" — a completion means "we DID this
// together" (attestation === true), so a *pre*-commitment can't be a completion;
// it lives on the device (readPlannedSet / persistPlannedSet), keyed by child.
//
// comingSoon tasks (e.g. Years 7–10 data missions during the beta) render greyed
// with a "Coming next term" chip and are NOT startable — no link, no state.

import { Link } from "react-router-dom";
import { Card, Chip, StrandBadge, STRAND_META, injectStyles } from "./ui.jsx";
import StateChip, { TASK_STATE } from "./StateChip.jsx";

// ── planned markers (device-local; see header note) ──────────────────────────
const plannedKey = (childId) => `maths.planned.${childId}`;

/** Read a child's planned-task ids as a Set (safe on bad/absent storage). */
export function readPlannedSet(childId) {
  try {
    const arr = JSON.parse(localStorage.getItem(plannedKey(childId)) || "[]");
    return new Set(Array.isArray(arr) ? arr.map(String) : []);
  } catch {
    return new Set();
  }
}

/** Persist a child's planned-task Set. Silently no-ops if storage is unavailable. */
export function persistPlannedSet(childId, set) {
  try {
    localStorage.setItem(plannedKey(childId), JSON.stringify([...set]));
  } catch {
    /* private-mode / quota — planned markers are non-critical UX */
  }
}

// ── state derivation ─────────────────────────────────────────────────────────
/**
 * Derive one task's state from the family's live snapshots.
 * - a completion for this task            → Done  (the completion is the record of "we did it")
 * - needs a dataset + planned or a dataset started, but not yet completed → Data to enter
 * - planned (no dataset needed / not yet) → Planned
 * - otherwise                             → Not started
 * @returns {'notStarted'|'planned'|'data'|'done'}
 */
export function deriveTaskState(task, { completions = [], datasets = [], planned } = {}) {
  const id = task.id;
  if (completions.some((c) => c && c.taskId === id)) return TASK_STATE.DONE;

  const isPlanned = planned instanceof Set ? planned.has(id) : !!(planned && planned[id]);
  const needsData = !!task.datasetSpec;
  const hasDataset = datasets.some((d) => d && d.taskId === id);

  // A data task the family has committed to (planned) or started collecting for,
  // but not yet finished, is waiting on the grown-up to type the numbers in.
  if (needsData && (isPlanned || hasDataset)) return TASK_STATE.DATA;
  if (isPlanned) return TASK_STATE.PLANNED;
  return TASK_STATE.NOT_STARTED;
}

// ── one task row ──────────────────────────────────────────────────────────────
function TaskRow({ task, childId, state }) {
  const minutes = Number.isFinite(task.estimatedMinutes) ? `${task.estimatedMinutes} min` : null;
  const body = (
    <div className="tl-row__body">
      <span className="tl-row__title">{task.title || task.id}</span>
      <span className="tl-row__meta">
        {minutes && <span>{minutes}</span>}
        {task.datasetSpec && <span>data task</span>}
      </span>
    </div>
  );

  if (task.comingSoon) {
    return (
      <li className="tl-row tl-row--soon" aria-disabled="true">
        {body}
        <Chip tone="neutral">Coming next term</Chip>
      </li>
    );
  }

  return (
    <li className="tl-row">
      <Link className="tl-row__link" to={`/grown-ups/child/${childId}/task/${task.id}`}>
        {body}
        <span className="tl-row__end">
          <StateChip state={state} />
          <span className="tl-row__chev" aria-hidden="true">›</span>
        </span>
      </Link>
    </li>
  );
}

// ── grouped list ──────────────────────────────────────────────────────────────
export default function TaskListByStrand({
  childId,
  tasks = [],
  completions = [],
  datasets = [],
  planned,
}) {
  if (!tasks.length) return null;

  const order = Object.keys(STRAND_META); // canonical strand order (number → probability)
  const known = new Set(order);
  const groups = order
    .map((strand) => ({ strand, items: tasks.filter((t) => t.strand === strand) }))
    .filter((g) => g.items.length);

  // Never silently drop a task authored under an unexpected strand.
  const rest = tasks.filter((t) => !known.has(t.strand));
  if (rest.length) groups.push({ strand: null, items: rest });

  return (
    <section className="tl" aria-label="Missions by strand">
      {groups.map((g) => (
        <div className="tl-group" key={g.strand || "more"}>
          <div className="tl-group__head">
            {g.strand ? <StrandBadge strand={g.strand} /> : <Chip>More</Chip>}
            <span className="tl-group__count">{g.items.length}</span>
          </div>
          <Card className="tl-card">
            <ul className="tl-list">
              {g.items.map((task) => (
                <TaskRow
                  key={task.id}
                  task={task}
                  childId={childId}
                  state={deriveTaskState(task, { completions, datasets, planned })}
                />
              ))}
            </ul>
          </Card>
        </div>
      ))}
    </section>
  );
}

// ── styles ────────────────────────────────────────────────────────────────────
const TL_CSS = `
.tl{display:flex;flex-direction:column;gap:1.4rem;}
.tl-group__head{display:flex;align-items:center;gap:.5rem;margin:0 0 .5rem .15rem;}
.tl-group__count{color:var(--ink-soft);font-size:.85rem;font-weight:600;}
.tl-card{padding:.35rem .5rem;}
.tl-list{list-style:none;margin:0;padding:0;}
.tl-row{border-bottom:1px solid var(--line);}
.tl-row:last-child{border-bottom:none;}
.tl-row__link{display:flex;align-items:center;gap:.75rem;min-height:52px;padding:.6rem .75rem;
  text-decoration:none;color:var(--ink);border-radius:10px;}
.tl-row__link:focus-visible{outline:2px solid var(--sky);outline-offset:-2px;}
.tl-row__body{flex:1 1 auto;min-width:0;display:flex;flex-direction:column;gap:.15rem;}
.tl-row__title{font-weight:600;color:var(--brand-ink);overflow-wrap:anywhere;}
.tl-row__meta{display:flex;flex-wrap:wrap;gap:.25rem .6rem;color:var(--ink-soft);font-size:.82rem;}
.tl-row__meta span+span::before{content:"·";margin-right:.6rem;color:var(--line);}
.tl-row__end{flex:0 0 auto;display:flex;align-items:center;gap:.4rem;}
.tl-row__chev{color:var(--ink-soft);font-size:1.25rem;line-height:1;}
.tl-row--soon{display:flex;align-items:center;gap:.75rem;min-height:52px;padding:.6rem .75rem;opacity:.62;}
.tl-row--soon .tl-row__title{color:var(--ink-soft);}
@media (hover:hover){.tl-row__link:hover{background:color-mix(in srgb,var(--brand) 6%,#fff);}}
`;

injectStyles("maths-tasklist-styles", TL_CSS);
